// =============================================================================
// app/src/lib/sugestao.ts — o que o boleto sugere, e por quê.
//
// ESTE ARQUIVO NASCEU DE UMA DIVERGÊNCIA. A decisão "que conta é esta?" vivia
// num `useMemo` de 35 linhas dentro de ContasPagarBoleto.tsx — sem teste — e a
// tela do LOTE (ContasPagarLote.tsx), que faz a mesma pergunta para nove folhas
// de uma vez, reimplementou uma versão MUTILADA: só chamava `contasParecidas`,
// sem reconhecer quem cobra, sem olhar a 2ª via já paga e sem a conta do mês.
//
// As duas cópias divergiram em QUATRO pontos, todos medidos em 28/09/2026 e
// todos do lado do lote:
//
//   1. a descrição sugerida era o NOME DO BANCO (`f.quem`), então cadastrar em
//      bloco criaria contas chamadas "Bradesco", "Itaú", "Sicredi";
//   2. a categoria era o literal 'fornecedores', jogando fora a que o segmento
//      do convênio informa (água é 2, energia é 3) — e, em cadeia, a busca pela
//      conta de luz do mês também não achava nada;
//   3. o fornecedor vinha `null`, mesmo quando o boleto anterior da mesma
//      cobrança dizia qual era;
//   4. **o pior**: não havia trava de conta PAGA. A tela de um boleto só tranca
//      com "já foi PAGO em… Não pague de novo"; o lote oferecia botão, gravava
//      o código de barras na conta quitada e TROCAVA o boleto guardado.
//
// Pura de propósito: o relógio entra por parâmetro, nada aqui toca React, rede
// ou arquivo. É o que permite testar a COMPOSIÇÃO — que era justamente o que
// não tinha cobertura nenhuma, enquanto as cinco peças isoladas têm 51 testes.
// =============================================================================
import type { CategoriaConta, ContaPagar } from '@core/types';
import {
  MESMA_COBRANCA, dadosDoCodigo, mesmoTitulo, semelhancaDeCobranca,
  type DadosDoCodigo,
} from './codigoBarras';
import { contasDoMes, contasParecidas, pagasParecidas, reconhecerCobranca } from './contasPagar';

/** O mínimo que uma conta precisa ter para o motor raciocinar sobre ela. */
export type ContaParaSugestao = Pick<ContaPagar,
  'id' | 'descricao' | 'vencimento' | 'valor_real' | 'valor_previsto' | 'pago_em'
  | 'codigo_barras' | 'categoria' | 'recorrencia_id' | 'fornecedor_id'>;

export interface SugestaoDoBoleto<T extends ContaParaSugestao> {
  /** O que o código de barras diz, sozinho. */
  dados: DadosDoCodigo;
  /** Quem cobra, e quantos boletos dele já entraram. Nulo = cobrança nova. */
  quem: { conta: T; vezes: number } | null;
  /** A categoria que a conta vai ter, em três degraus. */
  categoria: CategoriaConta;
  /** Onde procurar repetição — a do boleto anterior, ou as do segmento. */
  categorias: string[];
  /** 2.5d: em que categorias a MESMA cobrança já foi lançada (todos os boletos anteriores dela, não só os do nome que
   *  mais se repete). `categoria` é a única delas — a sugerida —, ou nula quando há mais de uma. Nulo = cobrança nova. */
  historico: HistoricoDaCobranca | null;
  /** Este código JÁ está numa conta: não é sugestão, é repetição. */
  mesmoCodigo: T | null;
  /** 2ª via de um título que já foi PAGO. Não se anexa, não se paga de novo. */
  tituloPago: T | null;
  /** "É esta?" — na ordem em que se deve perguntar. */
  candidatas: T[];
  /** Outras vias do mesmo título. */
  reemitidas: T[];
  /** Convênio sem data cujo valor bate com uma conta já quitada. */
  jaPaga: T[];
}

/**
 * A análise de UM boleto contra as contas que existem.
 *
 * A ordem das candidatas é a ordem da pergunta, e não é arbitrária: primeiro a
 * via antiga do mesmo título ainda aberta, depois a conta lançada sem código
 * com o mesmo valor, depois a que já foi paga (convênio sem data), e por último
 * — só quando nada mais apareceu — a conta fixa do mês.
 */
/** 2.5d: a contagem das categorias dos boletos anteriores da mesma cobrança (a régua de reconhecerCobranca). */
export interface HistoricoDaCobranca {
  categoria: CategoriaConta | null;
  vezes: number;
  total: number;
  categorias: CategoriaConta[];
}

export function historicoDaCobranca(
  linha: string,
  contas: Array<Pick<ContaPagar, 'codigo_barras' | 'categoria'>>,
): HistoricoDaCobranca | null {
  const por = new Map<CategoriaConta, number>();
  for (const c of contas) {
    if (!c.codigo_barras || c.codigo_barras === linha) continue;
    if (semelhancaDeCobranca(linha, c.codigo_barras) < MESMA_COBRANCA) continue;
    por.set(c.categoria, (por.get(c.categoria) ?? 0) + 1);
  }
  if (!por.size) return null;
  const categorias = [...por.keys()].sort();
  const unica = categorias.length === 1 ? categorias[0]! : null;
  return { categoria: unica, vezes: unica ? por.get(unica)! : 0, total: [...por.values()].reduce((a, b) => a + b, 0), categorias };
}

export function sugerirDoBoleto<T extends ContaParaSugestao>(
  linha: string,
  contas: T[],
  hoje: string,
): SugestaoDoBoleto<T> {
  const dados = dadosDoCodigo(linha, hoje);
  const quem = reconhecerCobranca(linha, contas, semelhancaDeCobranca, MESMA_COBRANCA);
  // A categoria que a conta vai ter: a dos boletos anteriores da mesma conta de
  // cobrança — 2.5d: só quando TODOS foram lançados na mesma (a CEEE cobra a
  // loja e a casa) —, senão a do segmento do convênio, senão fornecedor (é o
  // que a loja mais recebe em boleto de banco).
  const historico = historicoDaCobranca(linha, contas);
  const categoria = (historico?.categoria ?? dados.categoria ?? 'fornecedores') as CategoriaConta;
  // Onde procurar a conta repetida: as categorias de quem cobra, se há boleto
  // anterior; senão todas em que o segmento do convênio costuma estar.
  const categorias = historico ? historico.categorias
    : dados.categorias.length ? dados.categorias : [categoria];

  const r = contasParecidas(
    // Convênio sem data passa '' de propósito, NUNCA `hoje`: `hoje` é um
    // vencimento inventado, e faria a janela de três dias casar com qualquer
    // conta desta semana. Era o segundo defeito do lote.
    { valor: dados.valor, vencimento: dados.vencimento ?? '', codigo: linha, categorias },
    contas, mesmoTitulo,
  );
  // Convênio sem data: a conta paga do mesmo valor é, quase sempre, este
  // boleto já quitado — e ele não pode virar a conta do mês que vem.
  const jaPaga = dados.vencimento ? [] : pagasParecidas(categorias, dados.valor, hoje, contas);
  const tituloPago = r.mesmoTitulo.find((c) => c.pago_em) ?? null;

  const vistas = new Set<string>();
  const candidatas = [
    ...r.mesmoTitulo.filter((c) => !c.pago_em),
    ...r.parecidas.filter((c) => !c.codigo_barras),
    ...jaPaga,
    ...(dados.vencimento || jaPaga.length ? [] : contasDoMes(categorias, dados.valor, hoje, contas)),
  ].filter((c) => (vistas.has(c.id) ? false : (vistas.add(c.id), true)));

  return {
    dados, quem, categoria, categorias, historico,
    mesmoCodigo: r.mesmoCodigo, tituloPago, candidatas,
    reemitidas: r.mesmoTitulo, jaPaga,
  };
}

/** Um campo do formulário que veio de palpite, e não de gente. */
export type CampoSugerido = 'descricao' | 'categoria' | 'fornecedor';

export interface PreenchimentoSugerido {
  descricao: string;
  categoria: CategoriaConta;
  fornecedorId: string | null;
  /** Os campos que são PALPITE. A tela marca só estes. */
  palpites: CampoSugerido[];
  /** De onde veio o palpite, em uma frase, para a tela mostrar. */
  porque: string | null;
}

/**
 * O que preencher no formulário, e o que disso é chute.
 *
 * A descrição só é sugerida quando existe boleto anterior da mesma cobrança —
 * **nunca o nome do banco**. O nome que vem no código de barras é quem
 * RECEBE (Bradesco, Itaú, Sicredi), não o que se está pagando; usá-lo como
 * descrição foi o defeito que impediu o cadastro em bloco de existir.
 */
export function preenchimentoSugerido<T extends ContaParaSugestao>(
  s: SugestaoDoBoleto<T>,
): PreenchimentoSugerido {
  const base = s.quem?.conta;
  const h = s.historico;
  const palpites: CampoSugerido[] = [];
  if (base?.descricao) palpites.push('descricao');
  // A categoria é palpite quando veio do histórico (2.5d: "sugerida pelo
  // histórico" — o dono confirma ou troca), quando o histórico tem mais de uma
  // (o campo leva o segmento ou o padrão, e o dono escolhe) e quando é o
  // segmento do convênio. O 'fornecedores' sem nada que o diga é só o padrão.
  if (h || (s.dados.categoria ?? null) !== null) palpites.push('categoria');
  if (base?.fornecedor_id) palpites.push('fornecedor');

  const frases: string[] = [];
  if (base) {
    frases.push(`Os ${s.quem!.vezes} boleto${s.quem!.vezes > 1 ? 's' : ''} anterior${s.quem!.vezes > 1 ? 'es' : ''} desta cobrança `
      + `${s.quem!.vezes > 1 ? 'eram' : 'era'} “${base.descricao}”.`);
  } else if (s.dados.categoria && !h) {
    frases.push(`O convênio do código de barras é de ${s.dados.categoria}.`);
  }
  if (h?.categoria) frases.push(`Categoria sugerida pelo histórico desta cobrança (${h.vezes} de ${h.total} vezes): confirme ou troque.`);
  else if (h) frases.push('O histórico desta cobrança tem mais de uma categoria: escolha a desta conta.');
  const porque = frases.length ? frases.join(' ') : null;

  return {
    descricao: base?.descricao ?? '',
    categoria: s.categoria,
    fornecedorId: base?.fornecedor_id ?? null,
    palpites,
    porque,
  };
}

/**
 * A folha do lote pode entrar no "anexar todas"?
 *
 * SÓ quando o código de barras casa EXATAMENTE com uma conta, ela não está
 * paga e ainda não tem boleto. Palpite não entra em bloco — palpite se olha um
 * a um. E conta paga não entra de jeito nenhum: anexar nela grava o código na
 * conta quitada e troca o documento guardado.
 */
export function podeAnexarEmBloco<T extends ContaParaSugestao & { boleto?: unknown }>(
  s: SugestaoDoBoleto<T> | null,
): T | null {
  if (!s?.mesmoCodigo) return null;
  if (s.mesmoCodigo.pago_em) return null;
  if (s.mesmoCodigo.boleto) return null;
  return s.mesmoCodigo;
}
